import React from 'react';
import {
    UsergroupAddOutlined,
    ThunderboltFilled,
    CheckCircleFilled,
    CloseCircleFilled,
    ClockCircleFilled,
    TeamOutlined,
    DesktopOutlined,
    SwapOutlined,
    DollarCircleOutlined,
    DollarOutlined,
    LineChartOutlined,
    UserDeleteOutlined,
} from '@ant-design/icons';
import VerifiedSealIcon from '../common/verifiedSealIcon/VerifiedSealIcon';
import userSummaryStyles from '../common/analytics/styles/user-summary-table.module.scss';
import kpiCardStyles from '../common/analytics/styles/kpi-card.module.scss';
import { SCOPES } from '../../constants/scopes.constants';

/** Every Dashboard tab. `requiredScope` is checked in addition to
 * SCOPES.DASHBOARD_VIEW (the Page-level gate) — see
 * dashboard.controller.js#getAccessibleDashboardTabs, which filters this
 * down to what the current session can actually see. */
export const DASHBOARD_TABS = [
    { key: '2', label: 'Usage', title: { text: 'Usage', highlight: 'Analytics' }, description: 'Usage analytics', requiredScope: SCOPES.DASHBOARD_USAGE_VIEW },
    { key: '1', label: 'Enrollment', title: { text: 'Enrollment', highlight:'Analytics' }, description: 'Enrollment analytics', requiredScope: SCOPES.DASHBOARD_ENROLLMENT_VIEW },
    { key: '3', label: 'Monetization', title: { text: 'Monetization', highlight: 'Analytics' }, description: 'Monetization analytics', requiredScope: SCOPES.DASHBOARD_MONETIZATION_VIEW },
];

export const DEFAULT_TAB_KEY = DASHBOARD_TABS[0].key;

export const DASHBOARD_DEFAULT_TIMEFRAME = '3m';
export const USER_SUMMARY_PAGE_SIZE = 5;

/** Default (empty) shapes for the dashboard's async state slices. */
export const DEFAULT_USER_SUMMARY = {
    rows: [],
    pageInfo: { current: 1, perPage: USER_SUMMARY_PAGE_SIZE, total: 0 },
    loading: false,
};

/** Usage tab's Transaction Summary (advances list) — same shape as user summary. */
export const DEFAULT_TRANSACTION_SUMMARY = {
    rows: [],
    pageInfo: { current: 1, perPage: USER_SUMMARY_PAGE_SIZE, total: 0 },
    loading: false,
};

/** Total Sessions drill-down drawer (Usage tab) — session events + filters. */
export const DEFAULT_SESSIONS_DRAWER = {
    open: false,
    loading: false,
    events: [],
    count: 0,
    query: '',
    platform: 'all',
    type: 'all',
    dateRange: null,
};

export const DEFAULT_USER_ADOPTION_METRICS = {
    activeFrequency: 'monthly',
    data: {},
    loading: false,
};

export const DEFAULT_ENGAGEMENT_SUMMARY_METRICS = {
    activeFrequency: 'monthly',
    data: {},
    loading: false,
};

export const DEFAULT_COHORT_MODAL = {
    open: false,
    metric: null,
    users: [],
    count: 0,
    query: '',
    sortBy: 'name',
    loading: false,
};

/** Shared chart / segment colors for dashboard analytics configs. */
export const DASHBOARD_CHART_COLORS = {
    primary: '#0059c5',
    secondary: '#0073ff',
    accentLight: '#93c5fd',
    success: '#d1ffca',
    warning: '#fdd8ce',
    error: '#fdcece',
};

const DASHBOARD_DONUT_FALLBACK_COLORS = [
    DASHBOARD_CHART_COLORS.primary,
    DASHBOARD_CHART_COLORS.accentLight,
    DASHBOARD_CHART_COLORS.error,
    DASHBOARD_CHART_COLORS.success,
    DASHBOARD_CHART_COLORS.warning,
    DASHBOARD_CHART_COLORS.secondary,
];

export const DASHBOARD_DONUT_SEGMENT_COLORS = {
    payrollHealth: {
        success: DASHBOARD_CHART_COLORS.primary,
        fail: DASHBOARD_CHART_COLORS.error,
    },
    advancesByType: {
        instant: DASHBOARD_CHART_COLORS.primary,
        standard: DASHBOARD_CHART_COLORS.accentLight,
    },
    ddAcquisition: {
        noDds: DASHBOARD_CHART_COLORS.primary,
        dds: DASHBOARD_CHART_COLORS.accentLight,
    },
};

export function applyDashboardDonutColors(segments = [], colorMapKey) {
    const list = Array.isArray(segments) ? segments : [];
    const colorMap = DASHBOARD_DONUT_SEGMENT_COLORS[colorMapKey] || {};

    return list.map((segment, index) => ({
        ...segment,
        color: colorMap[segment.key] || DASHBOARD_DONUT_FALLBACK_COLORS[index % DASHBOARD_DONUT_FALLBACK_COLORS.length],
    }));
}

export const KPI_ICONS = {
    usersOptIn: <UsergroupAddOutlined />,
    usersEnrolled: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    activeUsers: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
};

// Adoption tiles that open the user-list drill-down modal, mapped to their
// cohort metric. Keyed by tile `key` so clickability does not depend on the
// backend also sending a `drilldown` field (that field, when present, wins).
export const KPI_DRILLDOWN = {
    usersOptIn: 'opted_in',
    usersEnrolled: 'enrolled',
    activeUsers: 'active',
};

export const USER_ADOPTION_FREQUENCIES = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
];

export const USER_ADOPTION_SERIES = [
    { key: 'optedIn', label: 'Opted-In', color: DASHBOARD_CHART_COLORS.primary },
    { key: 'enrolled', label: 'Enrolled', color: DASHBOARD_CHART_COLORS.secondary },
    { key: 'active', label: 'Active', color: DASHBOARD_CHART_COLORS.accentLight },
];

export const ENGAGEMENT_SUMMARY_SERIES = [
    { key: 'enrolled', label: 'Enrolled', color: DASHBOARD_CHART_COLORS.primary },
    { key: 'engaged', label: 'Engaged', color: DASHBOARD_CHART_COLORS.secondary },
    { key: 'active', label: 'Active', color: DASHBOARD_CHART_COLORS.accentLight },
];

export const ADVANCE_SUMMARY_TOGGLES = [
    { key: 'volume', label: 'Volume', isCurrency: true },
    { key: 'advances', label: 'Advances' },
];

export const VOLUME_COUNT_TOGGLES = [
    { key: 'volume', label: 'Volume', isCurrency: true },
    { key: 'count', label: 'Count' },
];

export const MONTH_QUARTER_TOGGLES = [
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
];

export const DAILY_WEEKLY_TOGGLES = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
];

export const MONETIZATION_KPI_ICONS = {
    totalAdvances: <UsergroupAddOutlined />,
    totalNonInterestIncome: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    totalDds: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    totalNewDirectDepositVolume: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    avgAdvancesPerUser: <SwapOutlined />,
    avgAdvanceSize: <DollarOutlined />,
    totalSessions: <DesktopOutlined />,
    inactiveUsers: <UserDeleteOutlined />,
};

export const USAGE_KPI_ICONS = {
    enrolledUsers: <TeamOutlined />,
    engagedUsers: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    activeUsers: <VerifiedSealIcon className={kpiCardStyles.iconSuccess} />,
    totalSessions: <DesktopOutlined />,
    totalAdvances: <SwapOutlined />,
    totalAdvanceVolume: <DollarCircleOutlined />,
    avgAdvanceSize: <DollarOutlined />,
    avgAdvancesPerUser: <SwapOutlined />,
    sessionsPerActiveUser: <LineChartOutlined />,
    inactiveUsers: <UserDeleteOutlined />,
};

export const ENROLLMENT_STATUS = {
    enrolled: { label: 'Enrolled', icon: <ThunderboltFilled /> },
    invalidOwnership: { label: 'Invalid Ownership', icon: <ThunderboltFilled /> },
    taxPending: { label: 'T&C Pending', icon: <ThunderboltFilled /> },
};

export const PROCESS_STATUS = {
    enrolled: { label: 'Enrolled', icon: <CheckCircleFilled /> },
    pending: { label: 'Pending', icon: <ClockCircleFilled /> },
    complete: { label: 'Complete', icon: <CheckCircleFilled /> },
    failed: { label: 'Failed', icon: <CloseCircleFilled /> },
};

export const ENROLLMENT_STATUS_CLASS = {
    enrolled: userSummaryStyles.statusEnrolled,
    invalidOwnership: userSummaryStyles.statusInvalid,
    taxPending: userSummaryStyles.statusTc,
};

export const PROCESS_STATUS_CLASS = {
    enrolled: userSummaryStyles.processEnrolled,
    pending: userSummaryStyles.processPending,
    complete: userSummaryStyles.processComplete,
    failed: userSummaryStyles.processError,
};
